import { Empty, LoadState, PageHeader, Panel, Pill } from "@/components/admin/AdminUI";
import { RULES, RunPanel, ruleHelp } from "@/components/admin/storage/RunPanel";
import MigrationPanel from "@/components/admin/storage/MigrationPanel";
import { useAdmin } from "@/admin/AuthContext";
import useAdminLoad from "@/hooks/useAdminLoad";
import { formatDate, humanize } from "@/lib/format";

const shortKey = (k) => (k && k.length > 42 ? `${k.slice(0, 18)}…${k.slice(-20)}` : k);

function BackendCard({ b }) {
  const ok = b.guard === "ok";
  return (
    <Panel className="p-6" data-testid="storage-backend-card">
      <h2 className="font-semibold">Where media is stored</h2>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Pill tone={b.backend === "r2" ? "success" : "warning"} data-testid="storage-backend">{b.backend === "r2" ? "Cloudflare R2" : b.backend === "emergent" ? "Old storage" : humanize(b.backend)}</Pill>
        <Pill data-testid="storage-env">{humanize(b.env)}</Pill>
        <Pill tone={ok ? "success" : "error"} data-testid="storage-guard">Guard: {ok ? "OK" : "refused"}</Pill>
      </div>
      <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-[8rem_1fr]">
        <dt className="cw-muted">Bucket</dt><dd className="font-mono" data-testid="storage-bucket">{b.bucket || "—"}</dd>
        {!ok && <><dt className="cw-muted">Reason</dt><dd className="cw-error" data-testid="storage-guard-reason">{b.guard}</dd></>}
      </dl>
    </Panel>
  );
}

function Totals({ totals }) {
  return (
    <Panel className="p-6" data-testid="storage-totals">
      <h2 className="font-semibold">Files by type</h2>
      <ul className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {Object.entries(totals || {}).map(([t, v]) => (
          <li key={t} className="bg-[var(--cw-surface-tertiary)] flex items-center justify-between rounded-xl px-4 py-3 text-sm" data-testid={`storage-total-${t}`}>
            <span>{humanize(t)}</span><span className="tabular-nums"><b>{v.files}</b> <span className="cw-muted">· {v.migrated} migrated</span></span></li>))}
      </ul>
    </Panel>
  );
}

function Queue({ data }) {
  return (
    <Panel className="p-6" data-testid="storage-queue">
      <h2 className="font-semibold">Deletion queue</h2>
      <ul className="mt-3 divide-y divide-border">
        {RULES.map((r) => {
          const q = data.queue?.[r] || {};
          return (
            <li key={r} className="grid gap-2 py-3 sm:grid-cols-[1fr_auto] sm:items-center" data-testid={`storage-rule-${r}`}>
              <div><p className="text-sm font-medium">{humanize(r)}</p><p className="cw-soft text-xs" data-testid={`storage-rule-help-${r}`}>{ruleHelp(r, data.settings)}</p></div>
              <p className="text-sm tabular-nums sm:text-right"><b>{q.due_now || 0}</b> due now · {q.upcoming || 0} upcoming · {data.held?.[r] || 0} held
                <span className="cw-muted block text-xs">{q.next_due_at ? `Next ${formatDate(q.next_due_at)}` : "Nothing scheduled"}</span></p>
            </li>);
        })}
      </ul>
    </Panel>
  );
}

function Recent({ rows }) {
  return (
    <Panel className="p-6" data-testid="storage-recent">
      <h2 className="font-semibold">Recent deletions</h2>
      {!rows?.length ? <Empty title="No deletions yet" text="Files the job deletes show here (latest 100)." testId="storage-recent-empty" /> : (
        <div className="mt-3 overflow-x-auto"><table className="w-full min-w-[640px] text-sm" data-testid="storage-recent-table">
          <thead><tr className="cw-muted border-b border-border text-left text-xs"><th className="py-2 pr-4 font-medium">File</th><th className="py-2 pr-4 font-medium">Rule</th><th className="py-2 pr-4 font-medium">When</th><th className="py-2 pr-4 font-medium">Result</th></tr></thead>
          <tbody>{rows.slice(0, 100).map((d, i) => (
            <tr key={`${d.key}-${i}`} className="border-b border-border last:border-0">
              <td className="py-2 pr-4 font-mono text-xs" title={d.key}>{shortKey(d.key)}</td><td className="py-2 pr-4">{humanize(d.rule)}</td>
              <td className="cw-muted whitespace-nowrap py-2 pr-4">{formatDate(d.at)}</td>
              <td className={`py-2 pr-4 ${String(d.result).startsWith("error") ? "cw-error" : ""}`}>{humanize(d.result)}</td></tr>))}</tbody>
        </table></div>)}
    </Panel>
  );
}

export default function Storage() {
  const owner = useAdmin().admin?.role === "owner";
  const { data, error, loading, reload } = useAdminLoad(owner ? "/admin/storage" : null);
  if (!owner) return <div data-testid="storage-page"><PageHeader title="Storage" /><Empty title="Owner only" text="Only the Owner can see storage." testId="storage-forbidden" /></div>;
  return (
    <div className="space-y-6" data-testid="storage-page">
      <PageHeader title="Storage" description="Where member media lives, what gets deleted and when." />
      <LoadState loading={loading && !data} error={error} onRetry={reload}>
        {data && <>
          <div className="grid gap-6 lg:grid-cols-2"><BackendCard b={data.backend || {}} /><RunPanel data={data} onDone={reload} /></div>
          <Queue data={data} />
          <Totals totals={data.totals} />
          <MigrationPanel initial={data.migration} r2={data.backend?.backend === "r2"} />
          <Recent rows={data.recent_deletions} />
        </>}
      </LoadState>
    </div>
  );
}
